import {
  Box,
  Container,
  Link,
  SimpleGrid,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';

import { MARKETING_PREVIEW_NAV } from './previewNav';

function scrollToPreviewSection(id: string) {
  const element = document.getElementById(id);
  if (!element) return;
  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.history.replaceState(null, '', `#${id}`);
}

function quickNavRevealThresholdPx() {
  return Math.min(window.innerHeight * 0.55, 520);
}

export function PreviewQuickNav() {
  const [visible, setVisible] = useState(false);
  const barBg = useColorModeValue(
    'rgba(249, 250, 251, 0.92)',
    'rgba(17, 24, 39, 0.92)',
  );
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const linkColor = useColorModeValue('gray.700', 'gray.200');
  const linkHoverBg = useColorModeValue('primary.50', 'whiteAlpha.150');
  const linkActiveColor = useColorModeValue('primary.700', 'primary.200');
  const buttonBorder = useColorModeValue('primary.300', 'primary.600');
  const buttonBg = useColorModeValue('white', 'whiteAlpha.100');

  useEffect(() => {
    const update = () => {
      setVisible(window.scrollY > quickNavRevealThresholdPx());
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <Box
      as="nav"
      aria-label="Product preview sections"
      aria-hidden={!visible}
      position="fixed"
      top={{ base: '72px', md: '80px' }}
      left={0}
      right={0}
      zIndex={90}
      bg={barBg}
      backdropFilter="blur(12px)"
      borderTop="1px solid"
      borderBottom="1px solid"
      borderColor={borderColor}
      pt={{ base: 4, md: 5 }}
      pb={{ base: 3, md: 4 }}
      transform={visible ? 'translateY(0)' : 'translateY(-110%)'}
      opacity={visible ? 1 : 0}
      pointerEvents={visible ? 'auto' : 'none'}
      transition="transform 0.25s ease, opacity 0.25s ease"
      boxShadow={visible ? '0 8px 24px rgba(0, 0, 0, 0.25)' : 'none'}
    >
      <Container maxW="container.content" px={{ base: 3, md: 4 }}>
        <SimpleGrid
          columns={{ base: 2, md: 4 }}
          gap={{ base: 2, md: 3 }}
          w="100%"
        >
          {MARKETING_PREVIEW_NAV.map((item) => (
            <Link
              key={item.id}
              href={`#${item.id}`}
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              gap={1}
              px={{ base: 2, md: 4 }}
              py={{ base: 3, md: 2.5 }}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={buttonBorder}
              bg={buttonBg}
              fontSize={{ base: 'sm', md: 'lg' }}
              fontWeight="bold"
              color={linkColor}
              textAlign="center"
              lineHeight="short"
              _hover={{
                textDecoration: 'none',
                color: linkActiveColor,
                bg: linkHoverBg,
                borderColor: 'primary.400',
              }}
              onClick={(event) => {
                event.preventDefault();
                scrollToPreviewSection(item.id);
              }}
            >
              <Box
                as={FiChevronDown}
                display={{ base: 'inline', md: 'none' }}
                boxSize={3}
                flexShrink={0}
                opacity={0.85}
              />
              <Text as="span" display={{ base: 'inline', md: 'none' }}>
                {item.shortLabel}
              </Text>
              <Text as="span" display={{ base: 'none', md: 'inline' }}>
                {item.label}
              </Text>
            </Link>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}
